'use client';

import { ENUM_UTILS, UIMarket } from '@velocity-exchange/common';
import { useCallback, useMemo, useState } from 'react';
import { useOrdersByUserKey } from 'src/hooks/query/accountTradingData/useOrders';
import { useVisibleAccountOrders } from 'src/hooks/query/accountTradingData/useVisibleOrders';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import useShowAccountValues from '../../hooks/useShowAccountValues';
import useVelocityStore from '../../stores/VelocityStore/useVelocityStore';
import TableStateWrapper from '../TableStateWrapper';
import { useTableFiltersState } from 'src/hooks/useTableFiltersState';
import { BalanceDisplayType } from 'src/@types/types';
import { AccountOrder } from './types';
import { OpenPerpOrderMobileCard } from './OpenPerpOrderMobileCard';
import { OpenOrdersFilters } from './OpenOrdersFilters';
import { cj } from 'src/utils/classNames';
import Checkbox from '../primitives/Checkbox/Checkbox';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import ExpandAllButton from 'src/components/Buttons/ExpandAllButton';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import useExpandableItemList from 'src/hooks/lists/expandable/useExpandableItemList';
import { useCancelOrders } from 'src/hooks/trade/cancel/useCancelOrders';
import { DataTable } from 'src/components/primitives/Table/DataTable';
import { getOpenOrdersColumns, OpenOrdersTableMeta } from './openOrdersColumns';
import ContentGuard from '../ContentGuard';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';

const EMPTY_MESSAGE = 'No open orders found';

const renderOrderTooltipContent = (order: AccountOrder) => (
	<pre>{JSON.stringify(order, null, 2)}</pre>
);

const OpenOrdersTable = (props: { overviewPage?: boolean }) => {
	const { toggleDateDisplayType, displayDatesAsTimestamp } =
		useDateDisplayState();
	const [sizeDisplayType, setSizeDisplayType] =
		useState<BalanceDisplayType>('base');

	const {
		showOnlyCurrentMarket = false,
		showPerpMarkets,
		showSpotMarkets,
		setShowOnlyCurrentMarket,
	} = useTableFiltersState(props);

	const userKey = useVelocityAccountStore((s) => s.currentUserKey);
	const openOrders: AccountOrder[] = useVisibleAccountOrders();
	const ordersLoaded = useOrdersByUserKey(userKey).data != null;
	const { cancelOrders } = useCancelOrders();
	const isMobile = useIsMobileScreenSize();
	const showAccountValues = useShowAccountValues();
	const devSwitchIsOn = useDevSwitchIsOn();

	const isOnTradePage = !props.overviewPage;

	const selectedMarket = useVelocityStore((s) => s.selectedMarket.current);

	const filterOrders = (
		orders: AccountOrder[],
		anyFiltersApplied: boolean,
		filterShowOnlyCurrentMarket: boolean,
		filterSelectedMarket: UIMarket
	) => {
		if (!anyFiltersApplied) return orders;

		if (filterShowOnlyCurrentMarket) {
			return orders.filter(
				(order) =>
					order.marketIndex === filterSelectedMarket.market.marketIndex &&
					ENUM_UTILS.match(order.marketType, filterSelectedMarket.marketType)
			);
		}

		return orders.filter((order) => {
			const uiMarket = new UIMarket(order.marketIndex, order.marketType);

			return (
				(showPerpMarkets && uiMarket.isPerp) ||
				(showSpotMarkets && uiMarket.isSpot)
			);
		});
	};

	const anyFiltersApplied =
		showOnlyCurrentMarket || showPerpMarkets || showSpotMarkets;

	const { displayOrders, filteredDisplayOrders } = useMemo(() => {
		if (!openOrders || !selectedMarket) {
			return { displayOrders: [], filteredDisplayOrders: [] };
		}

		const ordersForMarket = openOrders.filter(
			(order) =>
				order.marketIndex === selectedMarket.market.marketIndex &&
				ENUM_UTILS.toStr(order.marketType) == selectedMarket.marketType
		);

		const tempOrders = (() => {
			if (!ordersForMarket.length) {
				return openOrders;
			}

			return [
				...ordersForMarket,
				...(!showOnlyCurrentMarket
					? openOrders.filter(
							(order) =>
								order.marketIndex !== selectedMarket.market.marketIndex ||
								ENUM_UTILS.toStr(order.marketType) !== selectedMarket.marketType
						)
					: []),
			];
		})();

		const sortedDisplayOrders = [...tempOrders].sort(
			(orderA, orderB) => orderB.slot.toNumber() - orderA.slot.toNumber()
		);

		const filteredOrders = filterOrders(
			sortedDisplayOrders,
			anyFiltersApplied,
			showOnlyCurrentMarket,
			selectedMarket
		);

		return {
			displayOrders: sortedDisplayOrders,
			filteredDisplayOrders: filteredOrders,
		};
	}, [
		openOrders,
		selectedMarket?.market?.symbol,
		showOnlyCurrentMarket,
		showPerpMarkets,
		showSpotMarkets,
		anyFiltersApplied,
	]);

	const {
		areAllItemsExpanded,
		handleItemExpandedChange,
		handleExpandAllItems,
		handleCollapseAllItems,
	} = useExpandableItemList({
		tableId: 'open-orders',
		numItems: filteredDisplayOrders.length,
	});

	const handleCancelAllOrCurrentOrders = useCallback(async () => {
		const ids = (filteredDisplayOrders ?? []).map((order) => order.orderId);
		if (!ids.length) {
			return;
		}

		const toastId = `cancel-filtered-orders-${ids.join(',')}-${Date.now()}`;
		await cancelOrders(toastId, ...ids);
	}, [filteredDisplayOrders, cancelOrders]);

	const columns = useMemo(
		() =>
			getOpenOrdersColumns({
				onToggleDateDisplay: toggleDateDisplayType,
			}),
		[toggleDateDisplayType]
	);

	const tableMeta: OpenOrdersTableMeta = useMemo(
		() => ({
			displayDatesAsTimestamp,
			showAccountValues,
			onCancelAll: handleCancelAllOrCurrentOrders,
		}),
		[displayDatesAsTimestamp, showAccountValues, handleCancelAllOrCurrentOrders]
	);

	const MobileMarketsControls = (
		<div className="flex w-full items-center justify-between p-3">
			{isOnTradePage ? (
				<Checkbox.Default
					id="hide-other-assets-orders"
					checked={!!showOnlyCurrentMarket}
					onCheckedChange={() =>
						setShowOnlyCurrentMarket(!showOnlyCurrentMarket)
					}
					label="Hide others"
					dataTestId="orders-hide-others"
				/>
			) : (
				<OpenOrdersFilters overviewPage={props.overviewPage} />
			)}
			<div className="flex items-center gap-x-2xl">
				<TextButton
					variant="tertiary"
					size="sm"
					onClick={handleCancelAllOrCurrentOrders}
					dataTestId="orders-cancel-all"
				>
					Cancel All Orders
				</TextButton>
				{isMobile && (
					<ExpandAllButton
						areAllItemsExpanded={areAllItemsExpanded}
						totalCount={filteredDisplayOrders?.length ?? 0}
						onExpandAll={handleExpandAllItems}
						onCollapseAll={handleCollapseAllItems}
						dataTestId="orders-expand-all"
					/>
				)}
			</div>
		</div>
	);

	if (isMobile) {
		return (
			<div className={cj(props.overviewPage && 'mb-4')} id="open_orders_table">
				<TableStateWrapper
					requireAccountCreated
					records={displayOrders ?? []}
					emptyStateText={
						<div className="flex items-center justify-center">
							No open orders found
						</div>
					}
					requireWalletConnect
					id="open_orders_table"
				>
					<div className="bg-main-bg">{MobileMarketsControls}</div>
					<div
						className={cj(
							'w-full sm:max-h-full',
							MOBILE_TABLE_CARD_LIST_CLASSNAME
						)}
					>
						{filteredDisplayOrders.length > 0 ? (
							filteredDisplayOrders.map((order) => {
								const card = (
									<OpenPerpOrderMobileCard
										order={order}
										showAccountValues={showAccountValues}
										areAllItemsExpanded={areAllItemsExpanded}
										handleItemExpandedChange={handleItemExpandedChange}
										sizeDisplayType={sizeDisplayType}
										onToggleSizeDisplayType={() =>
											setSizeDisplayType(
												sizeDisplayType === 'base' ? 'notional' : 'base'
											)
										}
									/>
								);

								if (!devSwitchIsOn) {
									return <div key={order.orderId}>{card}</div>;
								}

								return (
									<Tooltip
										key={order.orderId}
										asChild
										decoration="none"
										content={<pre>{JSON.stringify(order, null, 2)}</pre>}
										contentClassName="max-w-[480px] max-h-[400px] overflow-auto"
									>
										<div>{card}</div>
									</Tooltip>
								);
							})
						) : (
							<MobileTableCard.EmptyPlaceholder>
								{EMPTY_MESSAGE}
							</MobileTableCard.EmptyPlaceholder>
						)}
					</div>
				</TableStateWrapper>
			</div>
		);
	}

	return (
		<ContentGuard requireAccount requireWallet isLoading={!ordersLoaded}>
			<DataTable
				columns={columns}
				data={filteredDisplayOrders}
				emptyMessage={EMPTY_MESSAGE}
				columnPinning={{ right: ['edit'] }}
				meta={tableMeta}
				dataTestId="open-orders-table"
				getRowTooltipContent={
					devSwitchIsOn ? renderOrderTooltipContent : undefined
				}
			/>
		</ContentGuard>
	);
};

export default OpenOrdersTable;
